import { useState, useMemo, useEffect, useCallback } from 'react';
import type { Company } from '@/types/company';
import { companyTypeOptions, allBusinessTags, allSourceTags } from '@/types/company';
import type { CallRecord } from '@/types/callRecord';
import { callStatusLabels, callFeedbackLabels } from '@/types/callRecord';
import { fetchCompanies, fetchCompanyStats, createCompany, updateCompany, deleteCompany, batchImportCompanies, assignCompany, type CreateCompanyPayload, type CompanyStats } from '@/api/companies';
import { fetchCallRecordsByCompany, createCallRecord, type CreateCallRecordPayload } from '@/api/callRecords';
import { fetchUsers } from '@/api/users';
import { useAuth } from '@/contexts/AuthContext';
import type { User } from '@/types/user';
import Layout from '@/components/feature/Layout';
import AddCompanyModal from '@/pages/home/components/AddCompanyModal';
import BatchImportModal from '@/pages/home/components/BatchImportModal';
import BatchTagBar from '@/pages/home/components/BatchTagBar';
import { useTagColors } from '@/hooks/useTagColors';
import AddCallRecordModal from '@/pages/telemarketing/components/AddCallRecordModal';

export default function HomePage() {
  const { colors: tagColors } = useTagColors();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === '系统管理员';
  const [salesUsers, setSalesUsers] = useState<User[]>([]);
  const [assigningId, setAssigningId] = useState<string | null>(null);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [stats, setStats] = useState<CompanyStats>({ total: 0, entity: 0, cert: 0, consult: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('全部');
  const [selectedBusinessTags, setSelectedBusinessTags] = useState<string[]>([]);
  const [selectedSourceTags, setSelectedSourceTags] = useState<string[]>([]);
  const [showTagDropdown, setShowTagDropdown] = useState<'business' | 'source' | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editCompany, setEditCompany] = useState<Company | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // 删除确认:单行删传 Company;批量删传 'batch';null 关闭
  const [deleteTarget, setDeleteTarget] = useState<Company | 'batch' | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callRecords, setCallRecords] = useState<CallRecord[]>([]);
  const [detailTab, setDetailTab] = useState<'info' | 'history'>('info');
  const [callPreset, setCallPreset] = useState<{
    companyId: string;
    companyName: string;
    contactId: string;
    contactName: string;
    contactPhone: string;
  } | null>(null);
  const pageSize = 8;

  // Load companies
  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCompanies({
        search: searchQuery || undefined,
        type: typeFilter !== '全部' ? typeFilter : undefined,
        businessTags: selectedBusinessTags.length > 0 ? selectedBusinessTags.join(',') : undefined,
        sourceTags: selectedSourceTags.length > 0 ? selectedSourceTags.join(',') : undefined,
        page: currentPage,
        pageSize,
      });
      setCompanies(result.data);
      setTotalCompanies(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载企业数据失败');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, typeFilter, selectedBusinessTags, selectedSourceTags, currentPage]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const statsData = await fetchCompanyStats();
      setStats(statsData);
    } catch {
      // stats are non-critical, ignore errors
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchUsers().then(setSalesUsers).catch(() => setSalesUsers([]));
  }, [isAdmin]);

  // Load call records when detail modal opens
  useEffect(() => {
    if (selectedCompany) {
      fetchCallRecordsByCompany(selectedCompany.id)
        .then(setCallRecords)
        .catch(() => setCallRecords([]));
    }
  }, [selectedCompany]);

  // Filtered companies for UI (server-side filtering now, but keep local for display)
  const filteredCompanies = companies;
  const pagedCompanies = filteredCompanies;

  // Stats
  const displayStats = useMemo(() => stats, [stats]);

  // Company-specific call records for detail modal
  const selectedCompanyRecords = useMemo(() => {
    if (!selectedCompany) return [];
    return callRecords.filter((r) => r.companyId === selectedCompany.id);
  }, [selectedCompany, callRecords]);

  const toggleTag = (type: 'business' | 'source', tag: string) => {
    if (type === 'business') {
      setSelectedBusinessTags((prev) =>
        prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
      );
    } else {
      setSelectedSourceTags((prev) =>
        prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
      );
    }
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setTypeFilter('全部');
    setSelectedBusinessTags([]);
    setSelectedSourceTags([]);
    setCurrentPage(1);
  };

  const hasFilters =
    searchQuery || typeFilter !== '全部' || selectedBusinessTags.length > 0 || selectedSourceTags.length > 0;

  // Handlers
  const handleAddCompany = async (data: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) => {
    setShowAddModal(false);
    try {
      await createCompany(data as CreateCompanyPayload);
      await loadCompanies();
      await loadStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : '新增企业失败');
    }
  };

  const handleUpdateCompany = async (id: string, data: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) => {
    setEditCompany(null);
    try {
      await updateCompany(id, data as CreateCompanyPayload);
      await loadCompanies();
      await loadStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新企业失败');
    }
  };

  const handleBatchImport = async (newCompanies: CreateCompanyPayload[]) => {
    try {
      await batchImportCompanies({ companies: newCompanies });
      await loadCompanies();
      await loadStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : '批量导入失败');
    }
  };

  // 删除:单个或批量。传入待删 id 数组,逐个调删除接口后刷新。
  const handleDelete = async (ids: string[]) => {
    if (ids.length === 0) return;
    setDeleting(true);
    try {
      await Promise.all(ids.map((id) => deleteCompany(id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      setDeleteTarget(null);
      // 若当前页删空且非首页,回退一页
      if (pagedCompanies.length === ids.length && currentPage > 1) {
        setCurrentPage((p) => p - 1);
      } else {
        await loadCompanies();
      }
      await loadStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败');
    } finally {
      setDeleting(false);
    }
  };

  const handleAssign = async (companyId: string, assignedTo: string) => {
    setAssigningId(companyId);
    try {
      const updated = await assignCompany(companyId, assignedTo);
      setCompanies((prev) => prev.map((c) => (c.id === companyId ? updated : c)));
      if (selectedCompany?.id === companyId) setSelectedCompany(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '分配失败');
    } finally {
      setAssigningId(null);
    }
  };

  const handleBatchTag = (businessTags: string[], sourceTags: string[]) => {
    // Batch tag would need a separate API endpoint
    // For now, show a message
    setSelectedIds(new Set());
  };

  const handleSelectAll = () => {
    const allPageIds = new Set(pagedCompanies.map((c) => c.id));
    if (pagedCompanies.every((c) => selectedIds.has(c.id))) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allPageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allPageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleEditFromDetail = () => {
    if (selectedCompany) {
      setEditCompany(selectedCompany);
      setSelectedCompany(null);
      setDetailTab('info');
    }
  };

  const closeDetail = () => {
    setSelectedCompany(null);
    setDetailTab('info');
  };

  const allPagedSelected = pagedCompanies.length > 0 && pagedCompanies.every((c) => selectedIds.has(c.id));
  const somePagedSelected = pagedCompanies.some((c) => selectedIds.has(c.id));

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-5">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">企业列表</h1>
            <p className="text-sm text-foreground-500 mt-1">管理和查询双碳行业客户资源</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 border border-accent-300 text-accent-600 hover:bg-accent-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-upload-line text-base"></i>
              批量导入
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-base"></i>
              新增企业
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setError(null); loadCompanies(); loadStats(); }}
                className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                重试
              </button>
              <button
                onClick={() => setError(null)}
                className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        )}

        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-building-2-line text-lg text-primary-600"></i>
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-foreground-900">{loading ? '-' : displayStats.total}</p>
                <p className="text-xs text-foreground-500">企业总数</p>
              </div>
            </div>
          </div>
          <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-building-line text-lg text-accent-600"></i>
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-foreground-900">{loading ? '-' : displayStats.entity}</p>
                <p className="text-xs text-foreground-500">实体企业</p>
              </div>
            </div>
          </div>
          <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-medal-line text-lg text-secondary-600"></i>
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-foreground-900">{loading ? '-' : displayStats.cert}</p>
                <p className="text-xs text-foreground-500">认证机构</p>
              </div>
            </div>
          </div>
          <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0">
                <i className="ri-group-line text-lg text-foreground-600"></i>
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-foreground-900">{loading ? '-' : displayStats.consult}</p>
                <p className="text-xs text-foreground-500">第三方咨询</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-4">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-0">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="搜索企业名称、联系人、电话、标签..."
                className="w-full h-10 pl-10 pr-4 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>

            {/* Type filter */}
            <div className="flex items-center gap-1.5 bg-background-100 rounded-lg p-1 flex-shrink-0">
              {companyTypeOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => {
                    setTypeFilter(opt);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    typeFilter === opt
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>

            {/* Tag filters */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Business tag */}
              <div className="relative">
                <button
                  onClick={() => setShowTagDropdown(showTagDropdown === 'business' ? null : 'business')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    selectedBusinessTags.length > 0
                      ? 'bg-accent-50 border-accent-300 text-accent-700'
                      : 'border-background-200 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                  }`}
                >
                  <i className="ri-price-tag-3-line text-sm"></i>
                  业务标签
                  {selectedBusinessTags.length > 0 && (
                    <span className="bg-accent-500 text-background-50 text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                      {selectedBusinessTags.length}
                    </span>
                  )}
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </button>
                {showTagDropdown === 'business' && (
                  <div className="absolute top-full mt-1 left-0 z-20 bg-background-50 rounded-xl border border-background-200/70 p-3 w-64 max-h-64 overflow-y-auto">
                    {allBusinessTags.map((tag) => (
                      <label
                        key={tag}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-background-100 cursor-pointer text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={selectedBusinessTags.includes(tag)}
                          onChange={() => toggleTag('business', tag)}
                          className="w-4 h-4 rounded border-background-300 text-accent-500 focus:ring-accent-400"
                        />
                        <span className="text-foreground-700">{tag}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Source tag */}
              <div className="relative">
                <button
                  onClick={() => setShowTagDropdown(showTagDropdown === 'source' ? null : 'source')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    selectedSourceTags.length > 0
                      ? 'bg-secondary-50 border-secondary-300 text-secondary-700'
                      : 'border-background-200 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                  }`}
                >
                  <i className="ri-folder-2-line text-sm"></i>
                  来源标签
                  {selectedSourceTags.length > 0 && (
                    <span className="bg-secondary-500 text-background-50 text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                      {selectedSourceTags.length}
                    </span>
                  )}
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </button>
                {showTagDropdown === 'source' && (
                  <div className="absolute top-full mt-1 left-0 z-20 bg-background-50 rounded-xl border border-background-200/70 p-3 w-64 max-h-64 overflow-y-auto">
                    {allSourceTags.map((tag) => (
                      <label
                        key={tag}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-background-100 cursor-pointer text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSourceTags.includes(tag)}
                          onChange={() => toggleTag('source', tag)}
                          className="w-4 h-4 rounded border-background-300 text-secondary-500 focus:ring-secondary-400"
                        />
                        <span className="text-foreground-700">{tag}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Clear filters */}
              {hasFilters && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-circle-line text-sm"></i>
                  清除
                </button>
              )}
            </div>
          </div>

          {/* Active tags display */}
          {(selectedBusinessTags.length > 0 || selectedSourceTags.length > 0) && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {selectedBusinessTags.map((tag) => (
                <span
                  key={`b-${tag}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-50 text-accent-700 rounded-md text-xs font-medium"
                >
                  {tag}
                  <button
                    onClick={() => toggleTag('business', tag)}
                    className="hover:text-accent-900 cursor-pointer"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </span>
              ))}
              {selectedSourceTags.map((tag) => (
                <span
                  key={`s-${tag}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-secondary-50 text-secondary-700 rounded-md text-xs font-medium"
                >
                  {tag}
                  <button
                    onClick={() => toggleTag('source', tag)}
                    className="hover:text-secondary-900 cursor-pointer"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Loading state */}
        {loading && (
          <div className="bg-background-50 rounded-xl border border-background-200/70 py-16 text-center">
            <div className="flex flex-col items-center gap-3">
              <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
              <p className="text-sm text-foreground-500">正在加载企业数据...</p>
            </div>
          </div>
        )}

        {/* Company table */}
        {!loading && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100/50">
                  <th className="text-left px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allPagedSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = somePagedSelected && !allPagedSelected;
                      }}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    企业名称
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    类型
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    负责人
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    分配销售
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider hidden md:table-cell whitespace-nowrap">
                    统一信用代码
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    业务标签
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider hidden lg:table-cell whitespace-nowrap">
                    联系人
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider hidden xl:table-cell whitespace-nowrap">
                    来源标签
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                {pagedCompanies.map((company) => {
                  const isSelected = selectedIds.has(company.id);
                  return (
                    <tr
                      key={company.id}
                      className={`hover:bg-background-100/50 transition-colors ${isSelected ? 'bg-primary-50/50' : ''}`}
                    >
                      <td className="px-4 py-3.5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(company.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                        />
                      </td>
                      <td
                        className="px-4 py-3.5 cursor-pointer"
                        onClick={() => setSelectedCompany(company)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <i className="ri-building-2-line text-primary-600"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-900 truncate max-w-[200px]">
                              {company.name}
                            </p>
                            <p className="text-xs text-foreground-500 truncate max-w-[200px] hidden sm:block">
                              {company.address}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td
                        className="px-4 py-3.5 cursor-pointer"
                        onClick={() => setSelectedCompany(company)}
                      >
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                            company.type === '实体企业'
                              ? 'bg-accent-50 text-accent-700'
                              : company.type === '认证机构'
                                ? 'bg-primary-50 text-primary-700'
                                : 'bg-secondary-50 text-secondary-700'
                          }`}
                        >
                          {company.type}
                        </span>
                      </td>
                      <td
                        className="px-4 py-3.5 cursor-pointer"
                        onClick={() => setSelectedCompany(company)}
                      >
                        <span className="text-sm text-foreground-700 whitespace-nowrap">
                          {company.responsiblePerson || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                        {isAdmin ? (
                          <select
                            value={company.assignedTo}
                            onChange={(e) => handleAssign(company.id, e.target.value)}
                            disabled={assigningId === company.id}
                            className="text-sm border border-background-200 rounded-md px-2 py-1 bg-background-50 focus:outline-none focus:border-primary-400 cursor-pointer disabled:opacity-60"
                          >
                            <option value="">未分配</option>
                            {salesUsers.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-sm text-foreground-700 whitespace-nowrap">
                            {company.assignedToName || '未分配'}
                          </span>
                        )}
                      </td>
                      <td
                        className="px-4 py-3.5 hidden md:table-cell cursor-pointer"
                        onClick={() => setSelectedCompany(company)}
                      >
                        <span className="text-sm text-foreground-600 font-mono">{company.creditCode}</span>
                      </td>
                      <td
                        className="px-4 py-3.5 cursor-pointer"
                        onClick={() => setSelectedCompany(company)}
                      >
                        <div className="flex flex-wrap gap-1 max-w-[180px]">
                          {company.businessTags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-accent-50 text-accent-700 rounded text-[11px] font-medium whitespace-nowrap"
                            >
                              {tagColors[tag] && (
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: tagColors[tag] }}></span>
                              )}
                              {tag}
                            </span>
                          ))}
                          {company.businessTags.length > 2 && (
                            <span className="inline-flex px-2 py-0.5 bg-background-100 text-foreground-500 rounded text-[11px] whitespace-nowrap">
                              +{company.businessTags.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className="px-4 py-3.5 hidden lg:table-cell cursor-pointer"
                        onClick={() => setSelectedCompany(company)}
                      >
                        {company.contacts.length > 0 ? (
                          <div className="flex items-center gap-2">
                            <div>
                              <p className="text-sm text-foreground-800">{company.contacts[0].name}</p>
                              <p className="text-xs text-foreground-500">{company.contacts[0].phone}</p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setCallPreset({
                                  companyId: company.id,
                                  companyName: company.name,
                                  contactId: company.contacts[0].id,
                                  contactName: company.contacts[0].name,
                                  contactPhone: company.contacts[0].phone,
                                });
                                setShowCallModal(true);
                              }}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 hover:bg-accent-200 transition-colors cursor-pointer flex-shrink-0"
                              title="通话记录"
                            >
                              <i className="ri-phone-line text-sm"></i>
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-foreground-400">暂无</span>
                        )}
                      </td>
                      <td
                        className="px-4 py-3.5 hidden xl:table-cell cursor-pointer"
                        onClick={() => setSelectedCompany(company)}
                      >
                        <div className="flex flex-wrap gap-1 max-w-[160px]">
                          {company.sourceTags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary-50 text-secondary-700 rounded text-[11px] font-medium whitespace-nowrap"
                            >
                              {tagColors[tag] && (
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: tagColors[tag] }}></span>
                              )}
                              {tag}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCompany(company);
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-eye-line text-sm"></i>
                            详情
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(company);
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                            title="删除企业"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {pagedCompanies.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
                          <i className="ri-search-line text-xl text-foreground-400"></i>
                        </div>
                        <p className="text-sm text-foreground-500">未找到匹配的企业</p>
                        <button
                          onClick={clearAllFilters}
                          className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                        >
                          清除筛选条件
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalCompanies > pageSize && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-background-200/70">
              <p className="text-xs text-foreground-500">
                共 <strong className="text-foreground-700">{totalCompanies}</strong> 条，
                第 {currentPage}/{Math.ceil(totalCompanies / pageSize)} 页
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: Math.ceil(totalCompanies / pageSize) }, (_, i) => i + 1).slice(0, 7).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      currentPage === page
                        ? 'bg-primary-500 text-background-50'
                        : 'border border-background-200 text-foreground-500 hover:bg-background-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(Math.ceil(totalCompanies / pageSize), p + 1))}
                  disabled={currentPage === Math.ceil(totalCompanies / pageSize)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Company detail modal */}
      {selectedCompany && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={closeDetail}
        >
          <div className="absolute inset-0 bg-foreground-950/40" />
          <div
            className="relative bg-background-50 rounded-2xl w-full max-w-[640px] max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="sticky top-0 bg-background-50 border-b border-background-200/70 px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-2-line text-lg text-primary-600"></i>
                </div>
                <div>
                  <h2 className="text-lg font-heading font-bold text-foreground-900">{selectedCompany.name}</h2>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded text-xs font-medium mt-0.5 ${
                      selectedCompany.type === '实体企业'
                        ? 'bg-accent-50 text-accent-700'
                        : selectedCompany.type === '认证机构'
                          ? 'bg-primary-50 text-primary-700'
                          : 'bg-secondary-50 text-secondary-700'
                    }`}
                  >
                    {selectedCompany.type}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleEditFromDetail}
                  className="flex items-center gap-1.5 px-3 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-edit-line text-sm"></i>
                  编辑
                </button>
                <button
                  onClick={closeDetail}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>

            {/* Modal body */}
            <div className="p-6 space-y-5">
              {/* Tabs */}
              <div className="flex items-center gap-1 bg-background-100 rounded-xl p-1 w-fit">
                <button
                  onClick={() => setDetailTab('info')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    detailTab === 'info'
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className="ri-information-line text-base"></i>
                  基本信息
                </button>
                <button
                  onClick={() => setDetailTab('history')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    detailTab === 'history'
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className="ri-history-line text-base"></i>
                  通话记录
                  <span className="text-xs text-foreground-400">({selectedCompanyRecords.length})</span>
                </button>
              </div>

              {detailTab === 'info' ? (
                <>
              {/* Basic info */}
              <div>
                <h3 className="text-sm font-heading font-semibold text-foreground-800 mb-3">基本信息</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-start gap-2">
                    <i className="ri-government-line text-foreground-400 mt-0.5"></i>
                    <div>
                      <p className="text-xs text-foreground-500">统一信用代码</p>
                      <p className="text-sm text-foreground-800 font-mono">{selectedCompany.creditCode}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-user-star-line text-foreground-400 mt-0.5"></i>
                    <div>
                      <p className="text-xs text-foreground-500">负责人</p>
                      <p className="text-sm text-foreground-800">{selectedCompany.responsiblePerson || '未分配'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-user-follow-line text-foreground-400 mt-0.5"></i>
                    <div>
                      <p className="text-xs text-foreground-500">分配销售</p>
                      {isAdmin ? (
                        <select
                          value={selectedCompany.assignedTo}
                          onChange={(e) => handleAssign(selectedCompany.id, e.target.value)}
                          disabled={assigningId === selectedCompany.id}
                          className="text-sm border border-background-200 rounded-md px-2 py-1 bg-background-50 focus:outline-none focus:border-primary-400 cursor-pointer disabled:opacity-60"
                        >
                          <option value="">未分配</option>
                          {salesUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-sm text-foreground-800">{selectedCompany.assignedToName || '未分配'}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-map-pin-line text-foreground-400 mt-0.5"></i>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm text-foreground-800">{selectedCompany.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-global-line text-foreground-400 mt-0.5"></i>
                    <div>
                      <p className="text-xs text-foreground-500">网址</p>
                      <a
                        href={`https://${selectedCompany.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {selectedCompany.website}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business tags */}
              <div>
                <h3 className="text-sm font-heading font-semibold text-foreground-800 mb-2">业务标签</h3>
                <div className="flex flex-wrap gap-1.5">
                  {selectedCompany.businessTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-50 text-accent-700 rounded-md text-xs font-medium"
                    >
                      {tagColors[tag] && (
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tagColors[tag] }}></span>
                      )}
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Source tags */}
              <div>
                <h3 className="text-sm font-heading font-semibold text-foreground-800 mb-2">来源标签</h3>
                <div className="flex flex-wrap gap-1.5">
                  {selectedCompany.sourceTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-50 text-secondary-700 rounded-md text-xs font-medium"
                    >
                      {tagColors[tag] && (
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tagColors[tag] }}></span>
                      )}
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Contacts */}
              <div>
                <h3 className="text-sm font-heading font-semibold text-foreground-800 mb-2">
                  联系人
                  <span className="text-foreground-500 font-normal ml-1">
                    ({selectedCompany.contacts.length}人)
                  </span>
                </h3>
                {selectedCompany.contacts.length > 0 ? (
                  <div className="space-y-2">
                    {selectedCompany.contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="flex items-center justify-between bg-background-100 rounded-lg px-4 py-2.5"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-medium text-foreground-600">
                              {contact.name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-800">{contact.name}</p>
                            <p className="text-xs text-foreground-500">{contact.position}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setCallPreset({
                                companyId: selectedCompany.id,
                                companyName: selectedCompany.name,
                                contactId: contact.id,
                                contactName: contact.name,
                                contactPhone: contact.phone,
                              });
                              setShowCallModal(true);
                            }}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 hover:bg-accent-200 transition-colors cursor-pointer flex-shrink-0"
                            title="通话记录"
                          >
                            <i className="ri-phone-line text-sm"></i>
                          </button>
                          <a
                            href={`tel:${contact.phone}`}
                            className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                          >
                            {contact.phone}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-400">暂无联系人信息</p>
                )}
              </div>

              {/* Business demands */}
              <div>
                <h3 className="text-sm font-heading font-semibold text-foreground-800 mb-2">
                  业务需求历史
                  <span className="text-foreground-500 font-normal ml-1">
                    ({selectedCompany.businessDemands.length}条)
                  </span>
                </h3>
                {selectedCompany.businessDemands.length > 0 ? (
                  <div className="space-y-2">
                    {selectedCompany.businessDemands.map((demand) => (
                      <div key={demand.id} className="bg-background-100 rounded-lg px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm text-foreground-800 flex-1">{demand.description}</p>
                          <span className="text-xs text-foreground-500 whitespace-nowrap">{demand.date}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs text-foreground-500">
                            <i className="ri-links-line mr-1"></i>
                            {demand.source}
                          </span>
                          <span className="inline-flex px-2 py-0.5 bg-secondary-50 text-secondary-700 rounded text-[11px] font-medium">
                            {demand.sourceTag}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-400">暂无业务需求记录</p>
                )}
              </div>
                </>
              ) : (
                <div>
                  {selectedCompanyRecords.length > 0 ? (
                    <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-background-200/70 bg-background-100/50">
                              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">联系人</th>
                              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">状态</th>
                              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">反馈</th>
                              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider hidden md:table-cell whitespace-nowrap">备注</th>
                              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">时间</th>
                              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider hidden sm:table-cell whitespace-nowrap">下次跟进</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-background-200/70">
                            {selectedCompanyRecords
                              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                              .map((record) => {
                                const statusInfo = callStatusLabels[record.status];
                                const feedbackInfo = record.feedback ? callFeedbackLabels[record.feedback] : null;
                                return (
                                  <tr key={record.id} className="hover:bg-background-100/50 transition-colors">
                                    <td className="px-4 py-3">
                                      <div className="flex items-center gap-2.5">
                                        <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center flex-shrink-0">
                                          <span className="text-xs font-medium text-foreground-600">{record.contactName.charAt(0)}</span>
                                        </div>
                                        <div className="min-w-0">
                                          <p className="text-sm font-medium text-foreground-800">{record.contactName}</p>
                                          <p className="text-xs text-foreground-500">{record.contactPhone}</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${statusInfo.color} whitespace-nowrap`}>
                                        <i className={`${statusInfo.icon} text-sm`}></i>
                                        {record.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      {feedbackInfo ? (
                                        <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${feedbackInfo.color} whitespace-nowrap`}>
                                          {record.feedback}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-foreground-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 hidden md:table-cell">
                                      <p className="text-xs text-foreground-600 line-clamp-2 max-w-[240px]">{record.notes}</p>
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="whitespace-nowrap">
                                        <p className="text-sm text-foreground-700">{record.callDate}</p>
                                        <p className="text-xs text-foreground-500">{record.callTime}</p>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 hidden sm:table-cell">
                                      {record.nextFollowUp ? (
                                        <span className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${
                                          record.nextFollowUp < new Date().toISOString().split('T')[0]
                                            ? 'text-amber-600 font-medium'
                                            : 'text-foreground-600'
                                        }`}>
                                          <i className="ri-calendar-line text-xs"></i>
                                          {record.nextFollowUp}
                                          {record.nextFollowUp < new Date().toISOString().split('T')[0] && (
                                            <span className="text-[10px] text-red-500">超期</span>
                                          )}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-foreground-400">-</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-background-50 rounded-xl border border-background-200/70 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
                          <i className="ri-history-line text-xl text-foreground-400"></i>
                        </div>
                        <p className="text-sm text-foreground-500">暂无通话记录</p>
                        <p className="text-xs text-foreground-400">拨打企业联系人后，通话记录会显示在这里</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add company modal */}
      <AddCompanyModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddCompany}
      />

      {/* Edit company modal */}
      <AddCompanyModal
        isOpen={!!editCompany}
        company={editCompany}
        onClose={() => setEditCompany(null)}
        onUpdate={handleUpdateCompany}
      />

      {/* Batch import modal */}
      <BatchImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleBatchImport}
      />

      {/* Batch tag bar */}
      {selectedIds.size > 0 && (
        <BatchTagBar
          selectedCount={selectedIds.size}
          onApplyTags={handleBatchTag}
          onClearSelection={() => setSelectedIds(new Set())}
          onDelete={() => setDeleteTarget('batch')}
        />
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div className="absolute inset-0 bg-foreground-950/40" />
          <div
            className="relative bg-background-50 rounded-2xl w-full max-w-[420px] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-lg text-red-500"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-heading font-bold text-foreground-900">确认删除</h3>
                <p className="text-sm text-foreground-600 mt-1">
                  {deleteTarget === 'batch'
                    ? `即将删除选中的 ${selectedIds.size} 家企业,及其联系人、标签关联。此操作不可撤销。`
                    : `即将删除「${deleteTarget.name}」及其联系人、标签关联。此操作不可撤销。`}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={() =>
                  handleDelete(deleteTarget === 'batch' ? Array.from(selectedIds) : [deleteTarget.id])
                }
                disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-red-400 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                {deleting ? (
                  <><i className="ri-loader-4-line animate-spin text-sm"></i>删除中...</>
                ) : (
                  <><i className="ri-delete-bin-line text-sm"></i>确认删除</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call record modal */}
      <AddCallRecordModal
        isOpen={showCallModal}
        onClose={() => {
          setShowCallModal(false);
          setCallPreset(null);
        }}
        onSubmit={async (data: Omit<CallRecord, 'id' | 'createdAt'>) => {
          setShowCallModal(false);
          setCallPreset(null);
          try {
            await createCallRecord(data as CreateCallRecordPayload);
            if (selectedCompany) {
              const records = await fetchCallRecordsByCompany(selectedCompany.id);
              setCallRecords(records);
            }
          } catch {
            // silently fail for call records
          }
        }}
        presetData={callPreset}
      />
    </Layout>
  );
}